import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  Header,
  Link,
  Pagination,
  Popover,
  PropertyFilter,
  PropertyFilterProps,
  SpaceBetween,
  StatusIndicator,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";
import { useCollection } from "@amzn/awsui-collection-hooks";
import { Investigation, Stage } from "../../lusca-search-api/generated-src";
import { InvestigationStatusChip } from "./InvestigationStatusChip";
import { waitingOnCodeReview } from "./CodeReviewBlocker";
import {
  HIGH_IMPACT_USD,
  NEEDS_OWNER_LABEL,
  NUMERIC_FILTER_OPERATORS,
  STATUS_LABELS,
  caseRules,
  commonRuleValue,
  formatTimestamp,
  investigationPath,
  isTerminal,
  AttentionInbox,
  needsAttention,
  propertyFilterI18n,
  shownStatus,
} from "./investigationUtils";
import { NeedsOwnerBadge } from "./NeedsOwnerBadge";
import { AssignNeedsOwnerModal } from "./AssignNeedsOwnerModal";
import { UserAvatar } from "./UserAvatar";
import { TrendSparkline } from "./TrendSparkline";
import { trendGrowth } from "./trendUtils";
import {
  READY_TO_RESOLVE,
  READY_TO_RESOLVE_LABEL,
  rowStatus,
} from "./readyToResolve";
import { GroupsFilterQuery, serializeGroupsFilter } from "./groupsFilter";
import { OneLine } from "../common/OneLine";

/**
 * A title as the table shows it: Java/AWS SDK exception classes lose their package
 * ("com.amazon.x.y.TerminalException" → "TerminalException") and a trailing " on any queue"
 * is dropped. The full title is the cell's hover text.
 */
function displayHeadline(title: string): string {
  return title
    .replace(/\b(?:[a-z][\w$]*\.){2,}([A-Z][\w$]*)/g, "$1")
    .replace(/ on any queue$/, "");
}

/** Who holds the case relative to the viewer (API/storage still say "unclaimed"). */
export type Ownership = "Mine" | "Needs an owner" | "Someone else";

const EMPTY_QUERY: PropertyFilterProps.Query = { operation: "and", tokens: [] };

/** A filter handed in from outside (URL, sidebar preset) as the PropertyFilter's query. */
export const toFilterQuery = (
  filter: GroupsFilterQuery | undefined
): PropertyFilterProps.Query =>
  filter
    ? {
        operation: filter.operation,
        tokens: filter.tokens.map((t) => ({
          propertyKey: t.propertyKey,
          operator: t.operator,
          value: t.value,
        })),
      }
    : EMPTY_QUERY;

/** The PropertyFilter query in the shareable shape (drops client-only token fields). */
export const fromFilterQuery = (
  query: PropertyFilterProps.Query
): GroupsFilterQuery | undefined =>
  query.tokens.length
    ? {
        operation: query.operation,
        tokens: query.tokens.map((t) => ({
          propertyKey: t.propertyKey,
          operator: t.operator,
          value: t.value as unknown,
        })),
      }
    : undefined;

export const OWNERSHIP_VALUES: Ownership[] = [
  "Mine",
  NEEDS_OWNER_LABEL as Ownership,
  "Someone else",
];

/** Flat, filterable projection of an investigation for the work-queue table. */
export interface InvestigationRow {
  id: string;
  title: string;
  queue: string;
  errorType: string;
  status: string;
  statusLabel: string;
  owner: string;
  ownership: Ownership;
  createdBy: string;
  items: number;
  /**
   * The worker is still claiming a large population: `items` is 0 for now and says nothing about
   * the case. The row shows "Claiming N items…" instead of a count until the marker completes.
   */
  claiming: number | null;
  /** Summed estimated USD impact of owned items; null until reconciliation has run. */
  impactUsd: number | null;
  /** Item change over the last 7 days of snapshots; null until two snapshots exist. */
  growth7d: number | null;
  redrives: number;
  returned: number;
  stale: "Yes" | "No";
  following: "Yes" | "No";
  /** Same rule as the "N need your attention" count: the viewer's case with unread notifications or gone stale. */
  attention: "Yes" | "No";
  updatedAtMs: number;
  investigation: Investigation;
}

// For a merged case a field only has "a" value when every branch agrees on it.
const ruleValue = (inv: Investigation, field: string): string =>
  commonRuleValue(caseRules(inv), field);

/**
 * Item count for the table. Active cases show the items they currently own in the live
 * registry (`assignedCount`). Resolving a case releases that ownership and zeroes the count, so
 * for resolved cases we show the frozen population instead — how many items the case covered.
 */
export function rowItemCount(inv: Investigation): number {
  if (isTerminal(inv.status)) {
    return inv.population?.count ?? inv.assignedCount ?? 0;
  }
  return inv.assignedCount ?? 0;
}

/**
 * Impact for the table. Active cases show the live impact of what they still own. A resolved
 * case's live figure has drained to ~$0 as its items left the DLQ, so — like rowItemCount — it
 * shows what the case covered: the peak impact in its (frozen) trend, or the last figure if higher.
 */
export function rowImpactUsd(inv: Investigation): number | null {
  const live =
    inv.impactUsd === undefined || inv.impactUsd === null
      ? null
      : inv.impactUsd;
  if (!isTerminal(inv.status)) return live;
  let peak = live;
  (inv.trend ?? []).forEach((p) => {
    if (typeof p.impact === "number" && (peak === null || p.impact > peak))
      peak = p.impact;
  });
  return peak;
}

/**
 * How many items a deferred claim is still working through, or null when none is pending. Only
 * an active case can be claiming; a closed one released its items and the marker is history.
 */
export function claimingCount(inv: Investigation): number | null {
  if (isTerminal(inv.status)) return null;
  if (inv.assignment?.status !== "PENDING") return null;
  return inv.assignment.requestedCount ?? inv.population?.count ?? 0;
}

const monitoringStatusLabel = (status: string, inv: Investigation): string =>
  status === READY_TO_RESOLVE
    ? READY_TO_RESOLVE_LABEL
    : STATUS_LABELS[status] || inv.status || "Unknown";

export function toRows(
  items: Investigation[],
  currentUser: string | null,
  /** The viewer's unread inbox, so the attention column agrees with the bell. */
  attention?: AttentionInbox
): InvestigationRow[] {
  return items.map((inv) => {
    const updated = inv.updatedAt ? new Date(inv.updatedAt).getTime() : NaN;
    return {
      id: inv.id,
      title: inv.title ?? "",
      queue: ruleValue(inv, "eventsourcekey"),
      errorType: ruleValue(inv, "error_type"),
      // A quiet monitored case lists (and filters) as Ready to resolve.
      status: rowStatus(inv, shownStatus(inv)),
      statusLabel: monitoringStatusLabel(rowStatus(inv, shownStatus(inv)), inv),
      owner: inv.owner ?? "",
      createdBy: inv.createdBy ?? "",
      ownership: !inv.owner
        ? (NEEDS_OWNER_LABEL as Ownership)
        : currentUser && inv.owner === currentUser
        ? "Mine"
        : "Someone else",
      items: rowItemCount(inv),
      claiming: claimingCount(inv),
      impactUsd: rowImpactUsd(inv),
      growth7d: trendGrowth(inv.trend),
      redrives: inv.redriveAttemptCount ?? 0,
      returned: inv.returnedCount ?? 0,
      stale: inv.isStale ? "Yes" : "No",
      following:
        currentUser && inv.followers?.includes(currentUser) ? "Yes" : "No",
      attention: needsAttention(inv, currentUser, attention) ? "Yes" : "No",
      updatedAtMs: Number.isNaN(updated) ? 0 : updated,
      investigation: inv,
    };
  });
}

const NUMERIC_OPERATORS = NUMERIC_FILTER_OPERATORS;

export const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  {
    key: "queue",
    propertyLabel: "Queue",
    groupValuesLabel: "Queues",
    operators: ["=", "!=", ":", "!:"],
  },
  {
    key: "status",
    propertyLabel: "Status",
    groupValuesLabel: "Statuses",
    operators: ["=", "!="],
  },
  {
    key: "ownership",
    propertyLabel: "Ownership",
    groupValuesLabel: "Ownership",
    operators: ["=", "!="],
  },
  {
    key: "owner",
    propertyLabel: "Owner",
    groupValuesLabel: "Owners",
    operators: ["=", "!=", ":"],
  },
  {
    key: "errorType",
    propertyLabel: "Error type",
    groupValuesLabel: "Error types",
    operators: ["=", "!=", ":", "!:"],
  },
  {
    key: "title",
    propertyLabel: "Title",
    groupValuesLabel: "Titles",
    operators: [":", "!:"],
  },
  {
    key: "items",
    propertyLabel: "Items",
    groupValuesLabel: "Item counts",
    operators: NUMERIC_OPERATORS,
  },
  {
    key: "impactUsd",
    propertyLabel: "Impact (USD)",
    groupValuesLabel: "Impact amounts",
    operators: NUMERIC_OPERATORS,
  },
  {
    key: "growth7d",
    propertyLabel: "7-day growth",
    groupValuesLabel: "7-day growth",
    operators: NUMERIC_OPERATORS,
  },
  {
    key: "redrives",
    propertyLabel: "Redrive attempts",
    groupValuesLabel: "Redrive attempt counts",
    operators: NUMERIC_OPERATORS,
  },
  {
    key: "returned",
    propertyLabel: "Returned items",
    groupValuesLabel: "Returned item counts",
    operators: NUMERIC_OPERATORS,
  },
  {
    key: "stale",
    propertyLabel: "Stale",
    groupValuesLabel: "Stale",
    operators: ["="],
  },
  {
    key: "following",
    propertyLabel: "Following",
    groupValuesLabel: "Following",
    operators: ["="],
  },
  {
    key: "attention",
    propertyLabel: "Needs my attention",
    groupValuesLabel: "Needs my attention",
    operators: ["="],
  },
];

/** Suggestion values for the enumerable properties, drawn from the loaded rows. */
export function filteringOptions(
  rows: InvestigationRow[]
): PropertyFilterProps.FilteringOption[] {
  const distinct = (pick: (r: InvestigationRow) => string) =>
    Array.from(new Set(rows.map(pick).filter(Boolean))).sort();
  return [
    ...distinct((r) => r.queue).map((value) => ({
      propertyKey: "queue",
      value,
    })),
    ...distinct((r) => r.errorType).map((value) => ({
      propertyKey: "errorType",
      value,
    })),
    ...distinct((r) => r.status).map((value) => ({
      propertyKey: "status",
      value,
      label:
        value === READY_TO_RESOLVE
          ? READY_TO_RESOLVE_LABEL
          : STATUS_LABELS[value] || value,
    })),
    ...distinct((r) => r.owner).map((value) => ({
      propertyKey: "owner",
      value,
    })),
    ...OWNERSHIP_VALUES.filter((v) => rows.some((r) => r.ownership === v)).map(
      (value) => ({
        propertyKey: "ownership",
        value,
      })
    ),
    { propertyKey: "stale", value: "Yes" },
    { propertyKey: "stale", value: "No" },
    { propertyKey: "following", value: "Yes" },
    { propertyKey: "following", value: "No" },
    { propertyKey: "attention", value: "Yes" },
    { propertyKey: "attention", value: "No" },
  ];
}

const PROPERTY_FILTER_I18N = propertyFilterI18n(
  "Filter investigations",
  "Filter by queue, status, owner, items…"
);

const DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences = {
  pageSize: 25,
  wrapLines: true,
  contentDisplay: [
    { id: "title", visible: true },
    { id: "queue", visible: false },
    { id: "status", visible: true },
    { id: "owner", visible: true },
    { id: "createdBy", visible: false },
    { id: "items", visible: true },
    { id: "impact", visible: true },
    { id: "trend", visible: true },
    { id: "redrives", visible: false },
    { id: "returned", visible: false },
    { id: "updated", visible: true },
  ],
};

const nowrap: React.CSSProperties = { whiteSpace: "nowrap" };

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/** "$12,345"; "—" until the first reconciliation has summed the case's items. */
export const formatImpactUsd = (value: number | null): string =>
  value === null ? "—" : usd.format(value);

/** Impact at or above this is emphasised in the table (re-exported from investigationUtils). */
export { HIGH_IMPACT_USD };

export interface RowTotals {
  items: number;
  /** Summed impact of the rows that have one; null when none has been reconciled yet. */
  impactUsd: number | null;
  /** Rows still waiting for their first reconciliation. */
  unreconciled: number;
  /** Rows whose population the worker is still claiming (their items are not counted yet). */
  claiming: number;
}

/** Item and impact totals for the (filtered) rows shown in a table header. */
export function summarise(rows: ReadonlyArray<InvestigationRow>): RowTotals {
  let impact: number | null = null;
  let unreconciled = 0;
  let items = 0;
  let claiming = 0;
  rows.forEach((r) => {
    items += r.items;
    if (r.claiming !== null) claiming += 1;
    if (r.impactUsd === null) unreconciled += 1;
    else impact = (impact ?? 0) + r.impactUsd;
  });
  return { items, impactUsd: impact, unreconciled, claiming };
}

/**
 * "12,345 items · $6,789 impact · 2 awaiting impact" for a header description. Cases created
 * since the last hourly reconciliation have no impact figure yet, so the total excludes them.
 */
export function describeTotals(totals: RowTotals, filtered: boolean): string {
  if (!totals.items && totals.impactUsd === null && !totals.claiming) return "";
  const parts = [
    `${totals.items.toLocaleString()} items`,
    `${formatImpactUsd(totals.impactUsd)} impact`,
  ];
  if (totals.claiming) {
    parts.push(`${totals.claiming} still claiming items`);
  }
  if (totals.unreconciled) {
    parts.push(`${totals.unreconciled} awaiting impact`);
  }
  return `${filtered ? "Matching: " : ""}${parts.join(" · ")}`;
}

const COLUMN_LABELS: Record<string, string> = {
  title: "Investigation",
  queue: "Queue",
  status: "Status",
  owner: "Owner",
  createdBy: "Created by",
  items: "Items",
  impact: "Impact",
  trend: "7-day trend",
  redrives: "Redrives",
  returned: "Returned",
  updated: "Updated",
};

/** Optional per-row action rendered in a trailing column (e.g. Take ownership). */
export interface RowAction {
  label: string;
  onClick: (investigation: Investigation) => void;
  /** Id of the row whose action is in flight (button shows a spinner). */
  loadingId?: string | null;
  /** Rows the action applies to (default: all). Others leave the cell empty. */
  visible?: (investigation: Investigation) => boolean;
}

function columnDefinitions(
  stage: Stage,
  onOpen: (id: string) => void,
  rowAction?: RowAction,
  highlightIds?: ReadonlySet<string>,
  /** Clicking an owner's name filters the table to them (omitted when there's no filter bar). */
  onOwnerClick?: (owner: string) => void,
  /** Clicking "Needs an owner" asks to take ownership of that row. */
  onNeedsOwnerClick?: (investigation: Investigation) => void,
  takingOwnershipId?: string | null
): TableProps.ColumnDefinition<InvestigationRow>[] {
  const actionColumn: TableProps.ColumnDefinition<InvestigationRow>[] =
    rowAction
      ? [
          {
            id: "action",
            header: "",
            width: 160,
            cell: (row) =>
              rowAction.visible && !rowAction.visible(row.investigation) ? (
                ""
              ) : (
                <Button
                  variant="inline-link"
                  onClick={() => rowAction.onClick(row.investigation)}
                  loading={rowAction.loadingId === row.id}
                  disabled={
                    !!rowAction.loadingId && rowAction.loadingId !== row.id
                  }
                  ariaLabel={`${rowAction.label}: ${row.title}`}
                >
                  {rowAction.label}
                </Button>
              ),
          },
        ]
      : [];
  return [
    {
      id: "title",
      header: COLUMN_LABELS.title,
      sortingField: "title",
      minWidth: 320,
      cell: (row) => {
        // Default titles are "<category>: <error type> on <queue>"; show everything before the
        // queue as the link and the queue underneath so neither has to fight for one line.
        const suffix = row.queue ? ` on ${row.queue}` : "";
        const headline = displayHeadline(
          suffix && row.title.endsWith(suffix)
            ? row.title.slice(0, -suffix.length)
            : row.title
        );
        return (
          // Break only inside a word that cannot fit on its own line.
          <div style={{ overflowWrap: "break-word" }} title={row.title}>
            <Link
              href={investigationPath(row.id, stage)}
              onFollow={(e) => {
                e.preventDefault();
                onOpen(row.id);
              }}
              fontSize="body-m"
            >
              {headline}
            </Link>
            {highlightIds?.has(row.id) && (
              <>
                {" "}
                <Badge color="green">Just claimed</Badge>
              </>
            )}
            {suffix && row.title.endsWith(suffix) && (
              <Box variant="small" color="text-body-secondary" display="block">
                {row.queue}
              </Box>
            )}
          </div>
        );
      },
    },
    {
      id: "queue",
      header: COLUMN_LABELS.queue,
      sortingField: "queue",
      cell: (row) => (
        <span style={{ overflowWrap: "anywhere" }}>{row.queue || "—"}</span>
      ),
    },
    {
      id: "status",
      header: COLUMN_LABELS.status,
      sortingField: "statusLabel",
      width: 210,
      cell: (row) => {
        const blocker =
          row.status === "BLOCKED" ? row.investigation.blocker : undefined;
        return (
          <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
            {blocker?.reason ? (
              // One line: the reason lives behind the chip rather than on a second row.
              <Popover
                triggerType="text"
                size="medium"
                dismissButton={false}
                header="Blocked"
                content={
                  <>
                    {blocker.reason}
                    {blocker.nextReviewDate
                      ? ` (review ${blocker.nextReviewDate})`
                      : ""}
                  </>
                }
              >
                <InvestigationStatusChip
                  status={row.status}
                  documentRedrive={row.investigation.documentRedrive}
                  waitingOn={waitingOnCodeReview(row.investigation)?.id}
                  wrapText={false}
                />
              </Popover>
            ) : (
              <InvestigationStatusChip
                status={row.status}
                documentRedrive={row.investigation.documentRedrive}
                waitingOn={waitingOnCodeReview(row.investigation)?.id}
                wrapText={false}
              />
            )}
          </SpaceBetween>
        );
      },
    },
    {
      id: "owner",
      header: COLUMN_LABELS.owner,
      sortingField: "owner",
      width: 170,
      cell: (row) => {
        if (row.ownership === NEEDS_OWNER_LABEL)
          return onNeedsOwnerClick ? (
            <NeedsOwnerBadge
              onClick={() => onNeedsOwnerClick(row.investigation)}
              loading={takingOwnershipId === row.id}
              actionLabel={`Take ownership of ${row.title}…`}
            />
          ) : (
            <NeedsOwnerBadge />
          );
        if (!row.owner) return row.owner;
        const { owner } = row;
        return (
          <span className="lusca-user-with-avatar">
            <UserAvatar alias={owner} />
            <span className="lusca-user-with-avatar__name">
              {onOwnerClick ? (
                <Button
                  variant="inline-link"
                  onClick={() => onOwnerClick(owner)}
                  ariaLabel={`Filter by owner ${owner}`}
                >
                  {owner}
                </Button>
              ) : (
                owner
              )}
            </span>
          </span>
        );
      },
    },
    {
      id: "createdBy",
      header: COLUMN_LABELS.createdBy,
      sortingField: "createdBy",
      width: 150,
      cell: (row) => <OneLine>{row.createdBy || "—"}</OneLine>,
    },
    {
      id: "items",
      header: COLUMN_LABELS.items,
      sortingField: "items",
      width: 110,
      cell: (row) => (
        <Box textAlign="right">
          {row.claiming !== null ? (
            <StatusIndicator type="in-progress">
              <span style={nowrap} data-testid="claiming-items">
                Claiming {row.claiming.toLocaleString()} items…
              </span>
            </StatusIndicator>
          ) : (
            <span style={nowrap}>{row.items.toLocaleString()}</span>
          )}
        </Box>
      ),
    },
    {
      id: "impact",
      header: COLUMN_LABELS.impact,
      // sortingField gives the column a stable identity across renders (so a second click
      // toggles direction); the comparator ranks unreconciled cases below $0.
      sortingField: "impactUsd",
      sortingComparator: (a, b) => (a.impactUsd ?? -1) - (b.impactUsd ?? -1),
      width: 130,
      cell: (row) => (
        <Box
          textAlign="right"
          color={
            row.impactUsd !== null && row.impactUsd >= HIGH_IMPACT_USD
              ? "text-status-error"
              : undefined
          }
          fontWeight={
            row.impactUsd !== null && row.impactUsd >= HIGH_IMPACT_USD
              ? "bold"
              : undefined
          }
        >
          <span
            style={nowrap}
            title={
              row.impactUsd === null
                ? "Awaiting impact — summed by the next hourly reconciliation"
                : undefined
            }
          >
            {formatImpactUsd(row.impactUsd)}
          </span>
        </Box>
      ),
    },
    {
      id: "trend",
      header: COLUMN_LABELS.trend,
      // No trend yet ranks below shrinking cases.
      sortingField: "growth7d",
      sortingComparator: (a, b) =>
        (a.growth7d ?? -Infinity) - (b.growth7d ?? -Infinity) ||
        a.items - b.items,
      width: 130,
      cell: (row) => <TrendSparkline trend={row.investigation.trend} />,
    },
    {
      id: "redrives",
      header: COLUMN_LABELS.redrives,
      sortingField: "redrives",
      width: 110,
      cell: (row) => <Box textAlign="right">{row.redrives || "—"}</Box>,
    },
    {
      id: "returned",
      header: COLUMN_LABELS.returned,
      sortingField: "returned",
      width: 110,
      cell: (row) =>
        row.returned ? (
          <Badge color="red">{row.returned.toLocaleString()}</Badge>
        ) : (
          <Box textAlign="right">—</Box>
        ),
    },
    {
      id: "updated",
      header: COLUMN_LABELS.updated,
      sortingField: "updatedAtMs",
      width: 180,
      cell: (row) => (
        <span style={nowrap}>
          {formatTimestamp(row.investigation.updatedAt)}
        </span>
      ),
    },
    ...actionColumn,
  ];
}

interface InvestigationTableProps {
  stage: Stage;
  items: Investigation[];
  currentUser: string | null;
  /** The viewer's unread inbox (attentionInbox), for the "Needs your attention" column. */
  attention?: AttentionInbox;
  loading: boolean;
  /** More pages exist on the server beyond what has been auto-loaded. */
  hasMore: boolean;
  onLoadMore: () => void;
  /** Label of the continuation button (default "Load more"; the overview uses "See all N"). */
  loadMoreLabel?: string;
  onOpen: (id: string) => void;
  /** Header title, e.g. "Active investigations"; the overview passes a link to the full page. */
  title: React.ReactNode;
  /** Registry / snapshot provenance shown under the title. */
  description?: React.ReactNode;
  /** Control shown left of the filter input (PropertyFilter customControl), e.g. All | Mine. */
  filterControl?: React.ReactNode;
  /** Right-hand header controls (the view switcher). */
  headerActions?: React.ReactNode;
  /**
   * Compact exception list: fixed columns, no filter bar, preferences or
   * pagination. Used for the small "Needs an owner" table.
   */
  compact?: boolean;
  /** Column ids shown in compact mode (title is always first). */
  compactColumns?: string[];
  /** Empty-state copy. */
  emptyText?: string;
  /** Omit the "items · impact" totals line (cancelled cases have no impact to sum). */
  hideTotals?: boolean;
  /** Header variant. */
  headerVariant?: "h2" | "h3";
  /** Trailing per-row action button. */
  rowAction?: RowAction;
  /**
   * Investigations to mark "Just claimed" next to the title: the ones the user took ownership of
   * during this visit, so a row that left the "Needs an owner" table is easy to find again here.
   */
  highlightIds?: ReadonlySet<string>;
  /**
   * Filter to apply, from the page URL or a sidebar preset. The table adopts it whenever it
   * changes; edits in the filter bar are reported through onFilterChange so the host can keep
   * the URL in step. Without these the filter is table-local.
   */
  filter?: GroupsFilterQuery;
  onFilterChange?: (filter: GroupsFilterQuery | undefined) => void;
  /**
   * Multi-select checkboxes. Selection is tracked by id (so it survives refetches and spans
   * pages); `onSelectedIdsChange` receives the full new selection.
   */
  selectedIds?: ReadonlySet<string>;
  onSelectedIdsChange?: (ids: string[]) => void;
  /**
   * Makes each row's "Needs an owner" badge a button opening an owner modal (defaults to the
   * viewer; another alias assigns it to them). `takingOwnershipId` is the row in flight.
   */
  onTakeOwnership?: (investigation: Investigation, assignTo?: string) => void;
  takingOwnershipId?: string | null;
}

export const InvestigationTable: React.FC<InvestigationTableProps> = ({
  stage,
  items,
  currentUser,
  attention,
  loading,
  hasMore,
  onLoadMore,
  loadMoreLabel = "Load more",
  onOpen,
  title,
  description,
  filterControl,
  headerActions,
  compact = false,
  compactColumns = [
    "title",
    "status",
    "createdBy",
    "items",
    "impact",
    "updated",
  ],
  emptyText = "No investigations in this view.",
  hideTotals = false,
  headerVariant = "h3",
  rowAction,
  highlightIds,
  filter: externalFilter,
  onFilterChange,
  selectedIds,
  onSelectedIdsChange,
  onTakeOwnership,
  takingOwnershipId,
}) => {
  const [confirmClaim, setConfirmClaim] = useState<Investigation | null>(null);
  const [preferences, setPreferences] =
    useState<CollectionPreferencesProps.Preferences>(DEFAULT_PREFERENCES);
  const rows = toRows(items, currentUser, attention);

  const {
    items: pageRows,
    allPageItems,
    filteredItemsCount,
    actions,
    collectionProps,
    propertyFilterProps,
    paginationProps,
  } = useCollection(rows, {
    propertyFiltering: {
      filteringProperties: FILTERING_PROPERTIES,
      filteringOptions: filteringOptions(rows),
      defaultQuery: toFilterQuery(externalFilter),
      empty: (
        <Box textAlign="center" color="inherit">
          <b>No investigations</b>
          <Box variant="p" color="inherit">
            {emptyText}
          </Box>
        </Box>
      ),
      noMatch: (
        <Box textAlign="center" color="inherit">
          <b>No matches</b>
          <Box variant="p" color="inherit">
            No investigations match the current filters.
          </Box>
          <Button
            onClick={() => {
              actions.setPropertyFiltering(EMPTY_QUERY);
              onFilterChange?.(undefined);
            }}
          >
            Clear filters
          </Button>
        </Box>
      ),
    },
    pagination: compact ? undefined : { pageSize: preferences.pageSize },
    sorting: {
      defaultState: {
        sortingColumn: { sortingField: "updatedAtMs" },
        isDescending: true,
      },
    },
  });

  const { query } = propertyFilterProps;
  const filtered = query.tokens.length > 0;
  // A filter edit (bar, clear button) updates the collection and tells the host in one go.
  const applyFilter = (next: PropertyFilterProps.Query) => {
    actions.setPropertyFiltering(next);
    onFilterChange?.(fromFilterQuery(next));
  };
  // Owner-name click: replace any owner/ownership filter with "Owner = <alias>", keep the rest.
  const filterByOwner = (owner: string) =>
    applyFilter({
      operation: query.operation,
      tokens: [
        ...query.tokens.filter(
          (t) => t.propertyKey !== "owner" && t.propertyKey !== "ownership"
        ),
        { propertyKey: "owner", operator: "=", value: owner },
      ],
    });
  // Adopt a new external filter (sidebar preset, back/forward) without echoing our own edits
  // back: compare wire forms, since the host may hand back the object we just reported.
  const externalKey = serializeGroupsFilter(externalFilter) ?? "";
  const currentKey = useMemo(
    () => serializeGroupsFilter(fromFilterQuery(query)) ?? "",
    [query]
  );
  useEffect(() => {
    if (externalKey !== currentKey) {
      actions.setPropertyFiltering(toFilterQuery(externalFilter));
    }
    // Only react to the external filter changing, not to our own edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalKey]);
  const selectable = !!onSelectedIdsChange;
  const selectedRows = selectable
    ? rows.filter((r) => selectedIds?.has(r.id))
    : [];
  const counter = selectedRows.length
    ? `(${selectedRows.length}/${rows.length})`
    : filtered
    ? `(${filteredItemsCount ?? 0}/${rows.length})`
    : `(${rows.length}${hasMore ? "+" : ""})`;
  const totals = summarise(allPageItems);

  const actionDisplay = rowAction ? [{ id: "action", visible: true }] : [];
  const columnDisplay = [
    ...(compact
      ? Object.keys(COLUMN_LABELS).map((id) => ({
          id,
          visible: id === "title" || compactColumns.includes(id),
        }))
      : preferences.contentDisplay ?? DEFAULT_PREFERENCES.contentDisplay ?? []),
    ...actionDisplay,
  ];

  return (
    <>
      <Table
        {...collectionProps}
        columnDefinitions={columnDefinitions(
          stage,
          onOpen,
          rowAction,
          highlightIds,
          compact ? undefined : filterByOwner,
          onTakeOwnership ? setConfirmClaim : undefined,
          takingOwnershipId
        )}
        columnDisplay={columnDisplay}
        items={pageRows}
        trackBy="id"
        selectionType={selectable ? "multi" : undefined}
        selectedItems={selectable ? selectedRows : undefined}
        onSelectionChange={
          selectable
            ? ({ detail }) =>
                onSelectedIdsChange(detail.selectedItems.map((r) => r.id))
            : undefined
        }
        ariaLabels={
          selectable
            ? {
                selectionGroupLabel: "Investigation selection",
                allItemsSelectionLabel: () => "Select all on this page",
                itemSelectionLabel: (_data, row) => `Select ${row.title}`,
              }
            : undefined
        }
        loading={loading}
        loadingText="Loading investigations"
        resizableColumns
        stickyHeader={!compact}
        wrapLines={compact ? true : preferences.wrapLines}
        variant="container"
        header={
          <Header
            variant={headerVariant}
            counter={counter}
            description={
              <SpaceBetween size="xxs">
                {description}
                {!hideTotals && describeTotals(totals, filtered) && (
                  <span
                    data-testid="table-totals"
                    style={
                      totals.impactUsd !== null &&
                      totals.impactUsd >= HIGH_IMPACT_USD
                        ? { fontWeight: "bold" }
                        : undefined
                    }
                  >
                    {describeTotals(totals, filtered)}
                  </span>
                )}
              </SpaceBetween>
            }
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {hasMore && (
                  <Button onClick={onLoadMore}>{loadMoreLabel}</Button>
                )}
                {headerActions}
              </SpaceBetween>
            }
          >
            {title}
          </Header>
        }
        filter={
          compact ? undefined : (
            <PropertyFilter
              {...propertyFilterProps}
              onChange={({ detail }) => applyFilter(detail)}
              customControl={filterControl}
              i18nStrings={PROPERTY_FILTER_I18N}
              countText={
                filtered
                  ? `${filteredItemsCount ?? 0} match${
                      filteredItemsCount === 1 ? "" : "es"
                    }`
                  : ""
              }
              expandToViewport
            />
          )
        }
        pagination={compact ? undefined : <Pagination {...paginationProps} />}
        preferences={
          compact ? undefined : (
            <CollectionPreferences
              title="Preferences"
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={{
                title: "Page size",
                options: [
                  { value: 25, label: "25 investigations" },
                  { value: 50, label: "50 investigations" },
                  { value: 100, label: "100 investigations" },
                ],
              }}
              wrapLinesPreference={{
                label: "Wrap lines",
                description: "Wrap long titles and queue names",
              }}
              contentDisplayPreference={{
                title: "Columns",
                options: Object.entries(COLUMN_LABELS).map(([id, label]) => ({
                  id,
                  label,
                  alwaysVisible: id === "title",
                })),
              }}
            />
          )
        }
      />
      {confirmClaim && onTakeOwnership && (
        <AssignNeedsOwnerModal
          stage={stage}
          investigation={confirmClaim}
          currentUser={currentUser}
          onDismiss={() => setConfirmClaim(null)}
          onConfirm={(alias) => onTakeOwnership(confirmClaim, alias)}
        />
      )}
    </>
  );
};

export default InvestigationTable;
